import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, TrendingUp, TrendingDown, Minus, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Loader from '../../components/ui/Loader';
import ipvService from '../../services/ipvService';
import { generarPDFIpv } from '../../utils/pdfGeneratorIpv';

export default function ResultadosIpv() {
  const { evaluacionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    cargarResultado();
  }, [evaluacionId]);

  const cargarResultado = async () => {
    try {
      setLoading(true);
      console.log('🔍 Cargando resultado interpretado para evaluación:', evaluacionId);
      
      const data = await ipvService.obtenerResultadoInterpretado(evaluacionId);
      console.log('✅ Resultado interpretado cargado:', data);
      
      setResultado(data);
    } catch (error) {
      console.error('❌ Error cargando resultado IPV:', error);
      toast.error('Error al cargar los resultados del examen IPV');
    } finally {
      setLoading(false);
    }
  };

  const getColorPorNivel = (nivel) => {
    switch (nivel) {
      case 'ALTO':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'PROMEDIO':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'BAJO':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-300';
    }
  };

  const getIconoPorNivel = (nivel) => {
    switch (nivel) {
      case 'ALTO':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'PROMEDIO':
        return <Minus className="w-5 h-5 text-yellow-600" />;
      case 'BAJO':
        return <TrendingDown className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const handleExportarPDF = async () => {
    try {
      toast.loading('Generando PDF...', { id: 'pdf-ipv' });
      await generarPDFIpv(resultado);
      toast.success('PDF generado exitosamente', { id: 'pdf-ipv' });
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar el PDF', { id: 'pdf-ipv' });
    }
  };

  // Array de escalas en orden
  const escalas = [
    { key: 'dgv', nombre: 'DGV - Disposición General para la Venta', icon: '🎯' },
    { key: 'receptividad', nombre: 'R - Receptividad', icon: '👂' },
    { key: 'agresividad', nombre: 'A - Agresividad', icon: '⚡' },
    { key: 'comprension', nombre: 'I - Comprensión', icon: '🧠' },
    { key: 'adaptabilidad', nombre: 'II - Adaptabilidad', icon: '🔄' },
    { key: 'controlSiMismo', nombre: 'III - Control de Sí Mismo', icon: '🎮' },
    { key: 'toleranciaFrustracion', nombre: 'IV - Tolerancia a la Frustración', icon: '💪' },
    { key: 'combatividad', nombre: 'V - Combatividad', icon: '🥊' },
    { key: 'dominancia', nombre: 'VI - Dominancia', icon: '👑' },
    { key: 'seguridad', nombre: 'VII - Seguridad', icon: '🛡️' },
    { key: 'actividad', nombre: 'VIII - Actividad', icon: '🏃' },
    { key: 'sociabilidad', nombre: 'IX - Sociabilidad', icon: '🤝' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (!resultado) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Brain className="w-16 h-16 text-neutral-400 mb-4" />
        <p className="text-neutral-600 mb-4">No se encontraron resultados para esta evaluación</p>
        <Button onClick={() => navigate('/admin/examenes-ipv')} variant="outline">
          Volver a Exámenes IPV
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/examenes-ipv')}
            className="gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Exámenes IPV
          </Button>

          <Card className="border-t-4 border-t-purple-600">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-neutral-800">
                      Resultados Examen IPV
                    </h1>
                    <p className="text-neutral-600 mt-1">
                      Evaluación #{resultado.evaluacionId} - {' '}
                      {resultado.nombrePostulante || `Postulante #${resultado.postulanteId}`}
                    </p>
                    <p className="text-sm text-neutral-500">
                      Fecha: {new Date(resultado.fechaEvaluacion).toLocaleDateString('es-PE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Resumen DGV - Destacado */}
        <div className="mb-6">
          <Card className="border-l-4 border-l-purple-600 shadow-lg">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="text-5xl">{escalas[0].icon}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-2xl font-bold text-neutral-800">
                      {resultado.dgv.nombreEscala}
                    </h3>
                    {getIconoPorNivel(resultado.dgv.nivel)}
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <Badge className={`text-base font-semibold border-2 px-4 py-2 ${getColorPorNivel(resultado.dgv.nivel)}`}>
                      {resultado.dgv.nivel}
                    </Badge>
                    <div className="flex gap-4 text-sm text-neutral-600">
                      <span><strong>Pd:</strong> {resultado.dgv.puntuacionDirecta}</span>
                      <span><strong>Pt:</strong> {resultado.dgv.puntuacionTransformada}</span>
                      <span><strong>Decatipo:</strong> {resultado.dgv.decatipo}</span>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-neutral-700 leading-relaxed">
                      {resultado.dgv.interpretacion}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Escalas Detalladas */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
            <span>📊</span>
            Análisis Detallado por Escala
          </h2>

          {escalas.slice(1).map((escala, index) => {
            const datos = resultado[escala.key];
            if (!datos) return null;

            return (
              <Card key={escala.key} className="hover:shadow-md transition-shadow">
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Icono */}
                    <div className="text-4xl flex-shrink-0">{escala.icon}</div>
                    
                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      {/* Header de la escala */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <h4 className="font-bold text-neutral-800 text-lg mb-1">
                            {datos.nombreEscala}
                          </h4>
                          <div className="flex items-center gap-4 text-sm text-neutral-600">
                            <span><strong>Pd:</strong> {datos.puntuacionDirecta}</span>
                            <span><strong>Pt:</strong> {datos.puntuacionTransformada}</span>
                            <span><strong>Decatipo:</strong> {datos.decatipo}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getIconoPorNivel(datos.nivel)}
                          <Badge className={`text-sm font-semibold border-2 ${getColorPorNivel(datos.nivel)}`}>
                            {datos.nivel}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Interpretación */}
                      <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200">
                        <p className="text-neutral-700 leading-relaxed text-sm">
                          {datos.interpretacion}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
            <div className="p-6 text-center">
              <p className="text-neutral-700 mb-4">
                Este reporte contiene el análisis completo del Inventario de Personalidad para Vendedores (IPV)
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/examenes-ipv')}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver a la lista
                </Button>
                <Button
                  onClick={handleExportarPDF}
                  className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                >
                  <Download className="w-4 h-4" />
                  Exportar PDF
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
